import { useState, useEffect } from 'react'
import {
  useEmailTemplate,
  useUpdateTemplate,
  useActivateTemplate,
  useDeactivateTemplate,
  usePreviewTemplate,
  useTestTemplate,
  useResetTemplate,
} from '@/hooks/useAdminEmails'
import { usePreviewEmailTemplate, useTestSendEmail, type EmailPreviewResponse } from '@/hooks/useEmailPreview'

interface EmailTemplateEditorProps {
  emailType: string
  onClose: () => void
}

export default function EmailTemplateEditor({ emailType, onClose }: EmailTemplateEditorProps) {
  const { data: template, isLoading } = useEmailTemplate(emailType)
  const [subjectTemplate, setSubjectTemplate] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [notes, setNotes] = useState('')
  const [showVariables, setShowVariables] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Enhanced preview state
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [previewData, setPreviewData] = useState<EmailPreviewResponse | null>(null)
  const [testRecipients, setTestRecipients] = useState<string>('')
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const updateMutation = useUpdateTemplate()
  const activateMutation = useActivateTemplate()
  const deactivateMutation = useDeactivateTemplate()
  const previewMutation = usePreviewTemplate()
  const testMutation = useTestTemplate()
  const resetMutation = useResetTemplate()

  // Enhanced preview mutations
  const previewWithVariablesMutation = usePreviewEmailTemplate()
  const testSendMultiMutation = useTestSendEmail()

  // Initialize form when template loads
  useEffect(() => {
    if (template) {
      setSubjectTemplate(template.subject_template)
      setHtmlBody(template.html_body)
      setNotes(template.notes || '')
      setIsDirty(false)
    }
  }, [template])

  // Track changes
  useEffect(() => {
    if (template) {
      const changed =
        subjectTemplate !== template.subject_template ||
        htmlBody !== template.html_body ||
        notes !== (template.notes || '')
      setIsDirty(changed)
    }
  }, [subjectTemplate, htmlBody, notes, template])

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      emailType,
      update: {
        subject_template: subjectTemplate,
        html_body: htmlBody,
        notes: notes || undefined,
      },
    })
    setIsDirty(false)
  }

  const handleActivate = async () => {
    if (isDirty) {
      await handleSave()
    }
    await activateMutation.mutateAsync(emailType)
  }

  const handleDeactivate = async () => {
    if (confirm('Deactivate custom template? The system will revert to the default template.')) {
      await deactivateMutation.mutateAsync(emailType)
    }
  }

  const handleReset = async () => {
    if (confirm('Reset template to default? This will clear all custom content.')) {
      await resetMutation.mutateAsync(emailType)
      setIsDirty(false)
    }
  }

  const handlePreview = async () => {
    try {
      const result = await previewMutation.mutateAsync(emailType)
      setPreviewHtml(result.html)
    } catch (error) {
      alert('Preview failed: ' + (error as Error).message)
    }
  }

  const handleTest = async () => {
    if (confirm('Send test email to your address?')) {
      try {
        await testMutation.mutateAsync(emailType)
        alert('Test email sent successfully!')
      } catch (error) {
        alert('Test email failed: ' + (error as Error).message)
      }
    }
  }

  const handlePreviewWithVariables = async () => {
    try {
      const result = await previewWithVariablesMutation.mutateAsync({
        emailType,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
      })
      setPreviewData(result)
    } catch (error) {
      alert('Preview failed: ' + (error as Error).message)
    }
  }

  const handleTestSendMulti = async () => {
    const emails = testRecipients.split(',').map((e) => e.trim()).filter(Boolean)

    if (emails.length === 0) {
      alert('Please enter at least one email address')
      return
    }

    if (!confirm(`Send test email to ${emails.length} recipient(s)?`)) return

    try {
      const result = await testSendMultiMutation.mutateAsync({
        emailType,
        recipients: emails,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
      })
      setTestResult({ success: true, message: `Sent to ${result.sent_count} recipients` })
    } catch (error: any) {
      setTestResult({ success: false, message: error.response?.data?.detail || error.message })
    }
  }

  if (isLoading) {
    return (
      <div className="fixed inset-y-0 right-0 w-full max-w-3xl bg-[#0a0e14] border-l border-[#FFB84D]/30
                      shadow-[-10px_0_50px_rgba(255,184,77,0.1)] z-50 flex items-center justify-center">
        <div className="text-[#FFB84D] font-mono animate-pulse">
          [LOADING DOSSIER...]
        </div>
      </div>
    )
  }

  if (!template) {
    return null
  }

  return (
    <>
      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-3xl bg-[#0a0e14] border-l border-[#FFB84D]/30
                      shadow-[-10px_0_50px_rgba(255,184,77,0.1)] z-50 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-[#0a0e14] border-b border-[#FFB84D]/20 p-6 z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📝</span>
              <div>
                <h2 className="text-xl font-mono uppercase tracking-wider text-[#FFB84D]">
                  {emailType.replace(/_/g, ' ')}
                </h2>
                <div className="text-xs text-[#8b949e] font-mono mt-1">
                  CLASSIFICATION: {template.is_active ? '[ACTIVE]' : '[STANDBY]'}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-[#8b949e] hover:text-[#FFB84D] transition-colors text-2xl"
            >
              ✕
            </button>
          </div>

          {isDirty && (
            <div className="mt-3 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
              <div className="text-xs text-yellow-400 font-mono">
                ⚠ UNSAVED CHANGES
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Subject */}
          <div>
            <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
              Subject Line
            </label>
            <input
              type="text"
              value={subjectTemplate}
              onChange={(e) => setSubjectTemplate(e.target.value)}
              className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                         text-[#e8eaed] font-mono text-sm
                         focus:outline-none focus:border-[#FFB84D]/50
                         focus:shadow-[0_0_15px_rgba(255,184,77,0.1)]"
              placeholder="Enter subject template..."
            />
          </div>

          {/* HTML Body */}
          <div>
            <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
              HTML Body
            </label>
            <textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              rows={20}
              className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                         text-[#e8eaed] font-mono text-sm leading-relaxed
                         focus:outline-none focus:border-[#FFB84D]/50
                         focus:shadow-[0_0_15px_rgba(255,184,77,0.1)]
                         resize-y"
              placeholder="Enter HTML body template or leave empty to use default..."
              style={{ fontFamily: '"Courier New", monospace' }}
            />
            <div className="mt-2 text-xs text-[#8b949e] font-mono">
              Leave empty to use system default. Custom body will be wrapped in branded email shell.
            </div>
          </div>

          {/* Variables Reference */}
          <div className="border border-[#2a3542] rounded-sm overflow-hidden">
            <button
              onClick={() => setShowVariables(!showVariables)}
              className="w-full bg-[#141c24]/50 px-4 py-3 text-left flex items-center justify-between
                         hover:bg-[#141c24] transition-colors"
            >
              <span className="text-sm font-mono uppercase tracking-wider text-[#FFB84D]">
                Available Variables
              </span>
              <span className="text-[#8b949e]">{showVariables ? '▼' : '▶'}</span>
            </button>

            {showVariables && template.variables_schema && (
              <div className="bg-[#0a0e14] p-4 space-y-2 border-t border-[#2a3542]">
                {Object.entries(template.variables_schema).map(([key, description]) => (
                  <div key={key} className="flex gap-3">
                    <code className="text-[#FFB84D] font-mono text-sm">
                      {`{{${key}}}`}
                    </code>
                    <span className="text-[#8b949e] text-sm">
                      {description as string}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
              Internal Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                         text-[#e8eaed] font-mono text-sm
                         focus:outline-none focus:border-[#FFB84D]/50"
              placeholder="Add notes about this template..."
            />
          </div>

          {/* Variable Customizer */}
          {template.variables_schema && Object.keys(template.variables_schema).length > 0 && (
            <div className="border border-[#2a3542] rounded-sm overflow-hidden">
              <button
                onClick={() => setShowVariables(!showVariables)}
                className="w-full px-4 py-3 bg-[#141c24] flex items-center justify-between
                           hover:bg-[#1a2230] transition-colors"
              >
                <span className="text-xs text-[#8b949e] font-mono uppercase tracking-wider">
                  Customize Variables for Preview
                </span>
                <span className="text-[#FFB84D]">{showVariables ? '▼' : '▶'}</span>
              </button>

              {showVariables && (
                <div className="p-4 bg-[#0a0e14] space-y-3">
                  {Object.keys(template.variables_schema).map((varName: string) => (
                    <div key={varName}>
                      <label className="block text-xs text-[#8b949e] font-mono mb-1">
                        {varName}
                      </label>
                      <input
                        type="text"
                        value={variables[varName] || ''}
                        onChange={(e) => setVariables({ ...variables, [varName]: e.target.value })}
                        className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                                   text-[#e8eaed] font-mono text-sm
                                   focus:outline-none focus:border-[#FFB84D]/50"
                        placeholder={`Sample ${varName}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview Panel */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-[#8b949e] font-mono uppercase tracking-wider">
                Preview
              </label>
              <button
                onClick={handlePreviewWithVariables}
                disabled={previewWithVariablesMutation.isPending}
                className="px-3 py-1 bg-[#FFB84D]/10 border border-[#FFB84D]/30 rounded
                           text-[#FFB84D] text-xs font-mono uppercase
                           hover:bg-[#FFB84D]/20 disabled:opacity-50"
              >
                {previewWithVariablesMutation.isPending ? 'Generating...' : 'Generate Preview'}
              </button>
            </div>

            {previewData && (
              <div className="border border-[#2a3542] rounded-sm overflow-hidden">
                <div className="bg-[#141c24] px-4 py-2 border-b border-[#2a3542]">
                  <div className="text-xs text-[#8b949e] font-mono">Subject:</div>
                  <div className="text-sm text-[#e8eaed] font-mono">{previewData.subject}</div>
                </div>
                <iframe
                  srcDoc={previewData.html}
                  className="w-full h-96 bg-white"
                  sandbox="allow-same-origin"
                  title="Email Preview"
                />
              </div>
            )}
          </div>

          {/* Test Send Panel */}
          <div>
            <label className="block text-xs text-[#8b949e] font-mono mb-2 uppercase tracking-wider">
              Test Send (Multi-Recipient)
            </label>
            <input
              type="text"
              value={testRecipients}
              onChange={(e) => setTestRecipients(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              className="w-full bg-[#141c24] border border-[#2a3542] rounded px-3 py-2
                         text-[#e8eaed] font-mono text-sm
                         focus:outline-none focus:border-[#FFB84D]/50 mb-2"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={handleTestSendMulti}
                disabled={testSendMultiMutation.isPending || !testRecipients.trim()}
                className="px-4 py-2 bg-[#FFB84D] rounded
                           text-[#0a0e14] font-mono text-sm uppercase font-bold
                           hover:bg-[#FFB84D]/90 disabled:opacity-50"
              >
                {testSendMultiMutation.isPending ? 'Sending...' : 'Send Test'}
              </button>
              {testResult && (
                <div className={`text-sm font-mono ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                  {testResult.message}
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          {template.last_tested_at && (
            <div className="text-xs text-[#8b949e] font-mono">
              Last tested: {new Date(template.last_tested_at).toLocaleString()}
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div className="sticky bottom-0 bg-[#0a0e14] border-t border-[#FFB84D]/20 p-6 space-y-3">
          {/* Primary Actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
              className="px-4 py-3 bg-[#FFB84D]/10 hover:bg-[#FFB84D]/20
                         border border-[#FFB84D]/30 text-[#FFB84D] font-mono text-sm
                         uppercase tracking-wider transition-all duration-200
                         disabled:opacity-30 disabled:cursor-not-allowed
                         hover:shadow-[0_0_15px_rgba(255,184,77,0.3)]"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Draft'}
            </button>

            <button
              onClick={handleActivate}
              disabled={!htmlBody || activateMutation.isPending}
              className="px-4 py-3 bg-green-500/10 hover:bg-green-500/20
                         border border-green-500/30 text-green-400 font-mono text-sm
                         uppercase tracking-wider transition-all duration-200
                         disabled:opacity-30 disabled:cursor-not-allowed
                         hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]"
            >
              {activateMutation.isPending ? 'Activating...' : template.is_active ? 'Save & Keep Active' : 'Activate'}
            </button>
          </div>

          {/* Secondary Actions */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              className="px-3 py-2 bg-[#141c24] hover:bg-[#1a1f2e]
                         border border-[#2a3542] hover:border-[#FFB84D]/30
                         text-[#8b949e] hover:text-[#FFB84D]
                         font-mono text-xs uppercase tracking-wider transition-all"
            >
              {previewMutation.isPending ? 'Loading...' : 'Preview →'}
            </button>

            <button
              onClick={handleTest}
              disabled={testMutation.isPending}
              className="px-3 py-2 bg-[#141c24] hover:bg-[#1a1f2e]
                         border border-[#2a3542] hover:border-[#FFB84D]/30
                         text-[#8b949e] hover:text-[#FFB84D]
                         font-mono text-xs uppercase tracking-wider transition-all"
            >
              {testMutation.isPending ? 'Sending...' : 'Test Send ✉'}
            </button>

            <button
              onClick={handleDeactivate}
              disabled={!template.is_active || deactivateMutation.isPending}
              className="px-3 py-2 bg-[#141c24] hover:bg-[#1a1f2e]
                         border border-[#2a3542] hover:border-yellow-500/30
                         text-[#8b949e] hover:text-yellow-400
                         font-mono text-xs uppercase tracking-wider transition-all
                         disabled:opacity-30"
            >
              {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
            </button>
          </div>

          {/* Destructive Action */}
          <button
            onClick={handleReset}
            disabled={resetMutation.isPending}
            className="w-full px-3 py-2 bg-red-500/10 hover:bg-red-500/20
                       border border-red-500/30 text-red-400
                       font-mono text-xs uppercase tracking-wider transition-all"
          >
            {resetMutation.isPending ? 'Resetting...' : 'Reset to Default ⚠'}
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Preview Modal */}
      {previewHtml && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-6"
          onClick={() => setPreviewHtml(null)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0a0e14] p-4 flex items-center justify-between border-b border-[#FFB84D]/30">
              <span className="font-mono text-[#FFB84D] uppercase tracking-wider">
                Email Preview
              </span>
              <button
                onClick={() => setPreviewHtml(null)}
                className="text-[#8b949e] hover:text-[#FFB84D] transition-colors"
              >
                ✕
              </button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  )
}
